import {
  decodeQueryParams,
  encodeQueryParams,
  QueryParamConfig,
  QueryParamConfigMap,
  DecodedValueMap,
  EncodedValueMap,
  StringParam,
} from 'serialize-query-params'
import qs from 'query-string'
import { useSearchParams } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { SimpleMap } from '@vendorflow/types'
import { pick } from 'lodash'

function convertURLSearchParamsToPlainObject(searchParams: URLSearchParams): SimpleMap<any> {
  const result: SimpleMap<string> = {}
  for (const [key, value] of searchParams.entries()) {
    result[key] = value
  }
  return result
}

function scrubKeysWithUndefinedValues(queryParams: SimpleMap<any>): SimpleMap<any> {
  const result: SimpleMap<any> = {}
  for (const [key, value] of Object.entries(queryParams)) {
    if (value !== undefined) {
      result[key] = value
    }
  }

  return result
}

export function useQueryParam<D extends QueryParamConfig<unknown>, D2 = D>(
  name: string,
  serializer: QueryParamConfig<D, D2> = StringParam as QueryParamConfig<any>
): [D2, (newValue: D2) => void] {
  const [searchParams, setSearchParams] = useSearchParams()
  const searchParam = searchParams.get(name)
  const [value, setValue] = useState<D2>()
  const setEncodedValue = useCallback(
    (value: D2) => {
      const prev = convertURLSearchParamsToPlainObject(searchParams)
      setSearchParams(scrubKeysWithUndefinedValues({ ...prev, [name]: encodeValue(value as unknown as D) }))
    },
    [setSearchParams, searchParams, encodeValue]
  )

  function encodeValue(value: D) {
    const dirtyEncoding = serializer.encode(value)

    if (Array.isArray(dirtyEncoding)) {
      return dirtyEncoding.filter((item) => item !== null) as string[]
    }

    return dirtyEncoding || undefined
  }

  useEffect(() => {
    const decoded = serializer.decode(searchParams.get(name))
    setValue(decoded)
  }, [searchParam])

  return [value as unknown as D2, setEncodedValue]
}

export function useQueryParams<T extends QueryParamConfigMap>(
  serializerConfigMap: T
): [DecodedValueMap<T>, (changes: Partial<DecodedValueMap<T>>) => void] {
  const initialState = useMemo(() => {
    const keys = Object.keys(serializerConfigMap)
    const result: any = {}
    for (const key of keys) {
      result[key] = undefined
    }

    return result
  }, [])
  const [searchParams, setSearchParams] = useSearchParams()
  const [value, setValue] = useState<DecodedValueMap<T>>(initialState)
  const getPertinentParams = useCallback(
    (searchParams: URLSearchParams) => {
      const parsed = qs.parse(searchParams.toString())
      const keys = Object.keys(serializerConfigMap)
      return pick(parsed, keys) as Partial<EncodedValueMap<T>>
    },
    [searchParams, serializerConfigMap]
  )
  const setEncodedValue = useCallback(
    (value: Partial<DecodedValueMap<T>>) => {
      const prev = convertURLSearchParamsToPlainObject(searchParams)
      const curr = scrubKeysWithUndefinedValues({ ...prev, ...encodeQueryParams(serializerConfigMap, value) })
      setSearchParams(curr)
    },
    [searchParams, setSearchParams, serializerConfigMap]
  )

  useEffect(() => {
    const decoded = getDecodedPertinentParams(searchParams)
    setValue((prev) => ({ ...prev, ...decoded }))
  }, [searchParams])

  function getDecodedPertinentParams(searchParams: URLSearchParams) {
    return decodeQueryParams(serializerConfigMap, getPertinentParams(searchParams))
  }

  return [value, setEncodedValue]
}
